import { useEffect, useState, useContext, useCallback, FC } from 'react'
import { Loader } from 'components'

import { User } from 'UnifiedLogin/types'
import { getStorage, deleteStorage } from 'utils/storage'
import { USER_TOKEN_STORAGE_NAME } from 'constants/index'
import readPersonDocuments from 'AppCitizen/services/calls/person/document/readDocuments'
import AuthenticationContextContext from './Context'
import checkUserToken from './services/calls/check'
import logoutUser from './services/calls/logout'
import { UnifiedPerson } from 'modules/CitizenProfile/types'

type Props = {
  children: JSX.Element
}

const AuthenticationProvider: FC<Props> = ({ children }) => {
  const [loadingContent, setLoadingContent] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [unifiedPerson, setUnifiedPerson] = useState<UnifiedPerson | null>(null)
  const [userBond, setUserBond] = useState(null)
  const [userBondRequest, setUserBondRequest] = useState(null)

  function deleteAllCookies() {
    const allCookies = document.cookie.split(';')
    for (let i = 0; i < allCookies.length; i++)
      document.cookie = allCookies[i] + '=;expires=' + new Date(0).toUTCString()
  }

  const logout = useCallback(async () => {
    deleteAllCookies()
    deleteStorage(USER_TOKEN_STORAGE_NAME)
    const response = await logoutUser()
    if (response) {
      setUser(null)
    }
    return true
  }, [])

  const fillUser = useCallback((userInformation: any) => {
    setUser(userInformation.person)
    setUnifiedPerson(userInformation.unified_person)
    setUserBond(userInformation.bond)
    setUserBondRequest(userInformation.bond_request)
  }, [])

  const checkToken = useCallback(async () => {
    const userToken = getStorage(USER_TOKEN_STORAGE_NAME)
    if (userToken) {
      const response = await checkUserToken()

      if (response?.header?.success) {
        const responseDocuments = await readPersonDocuments()
        const body = response.body
        body.person = { ...body?.person, ...responseDocuments?.body?.person }
        body.unified_person = { ...body?.unified_person }
        fillUser(body)
      } else {
        if (response?.header?.code === 'D9u90AOeIp') {
          logout()
        }
      }
    }
    setLoadingContent(false)
  }, [])

  useEffect(() => {
    checkToken()
  }, [])

  if (loadingContent) {
    return <Loader text="Verificando credenciais" height="100vh" />
  }

  return (
    <AuthenticationContextContext.Provider
      value={{
        fillUser,
        user,
        setUser,
        unifiedPerson,
        setUnifiedPerson,
        userBond,
        userBondRequest,
        setUserBondRequest,
        logout,
      }}
    >
      {children}
    </AuthenticationContextContext.Provider>
  )
}

export const useAuthentication = () => useContext(AuthenticationContextContext)

export default AuthenticationProvider
